import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { clearNewlyEarnedBadges } from 'store/relicVault';
import * as api from 'utils/relicVault/collectionApi';
import { coinsForRarity } from 'firstVisit/badgeRewards';
import { emit } from 'firstVisit/events';

/**
 * Badges the server awarded while the learner was playing, one at a time, for the badge moment.
 *
 * Badges are earned server-side - a streak reaching its next milestone, a lesson finished, a first-visit step -
 * so the app only finds out when it next reads the vault. Every newly earned badge gets its moment (Sol
 * player-tour Q4 A: fly-in, name, coins and the coin sound; the old toast is gone).
 *
 * The first read after opening the world is a baseline, so nothing is announced for badges already earned
 * (see hydrateRelicVault).
 */
// held: a moment waits (the tutor is talking - owner prototype: First Friend comes after the conversation, never over it).
export function useBadgeAnnouncements({ token, held = false } = {}) {
  const dispatch = useDispatch();
  const newlyEarnedBadgeIds = useSelector(state => state.relicVault.newlyEarnedBadgeIds);
  const badgeCategories = useSelector(state => state.relicVault.badgeCategories);
  const [queue, setQueue] = useState([]);

  useEffect(() => {
    if (!newlyEarnedBadgeIds.length) return;
    const ids = [...newlyEarnedBadgeIds];
    dispatch(clearNewlyEarnedBadges());

    const describe = categories => {
      const byId = new Map();
      for (const category of categories || []) {
        for (const badge of category.badges || []) byId.set(badge.badgeId, badge);
      }
      return ids.map(badgeId => {
        const badge = byId.get(badgeId) || {};
        return {
          badgeId,
          name: badge.name || 'New badge',
          imageUrl: badge.imageUrl || null,
          coins: coinsForRarity(badge.rarity),
          // the line under the name in the moment ("Watch your first slide")
          description: badge.requirementText || '',
        };
      });
    };

    if (badgeCategories.length) {
      setQueue(current => [...current, ...describe(badgeCategories)]);
      return;
    }
    // The catalog names and pictures the badge; a moment without them still shows.
    api
      .fetchBadgeCatalog({ token })
      .catch(() => [])
      .then(categories => setQueue(current => [...current, ...describe(categories)]));
  }, [badgeCategories, dispatch, newlyEarnedBadgeIds, token]);

  const playing = queue.length > 0 && !held;
  useEffect(() => {
    emit('badge-moment', { playing });
  }, [playing]);

  const finished = useCallback(() => setQueue(current => current.slice(1)), []);

  return { current: held ? null : queue[0] || null, finished };
}

export default useBadgeAnnouncements;
